import { useQuery } from "@tanstack/solid-query";
import type { RunSummary } from "contracts/api/crawl/run-read.responses";
import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { CRAWL_RUN_STATUS } from "contracts/enums/core";
import { api } from "../../store/api";
import { pluralize } from "../../ui/format";
import { contentFitnessPageCoverage } from "./scanModel";

export type ScanCoverage = {
  inventoryPages: number;
  completedPages: number | null;
  failedPages: number | null;
  skippedPages: number | null;
  assessedPages: number;
  scoredPages: number;
};

export function createCompletedRunForReport(
  websiteId: string,
  data: () => OverviewResponse | null,
) {
  const runs = useQuery(() => ({
    queryKey: ["runs", websiteId],
    queryFn: () => api.listRuns(websiteId),
  }));
  return () => {
    const reportStartedAt = data()?.lastRun?.startedAt?.getTime();
    if (reportStartedAt === undefined) return null;
    return (
      runs.data?.runs.find(
        (run) =>
          run.status === CRAWL_RUN_STATUS.COMPLETED &&
          run.startedAt.getTime() === reportStartedAt,
      ) ?? null
    );
  };
}

export function scanCoverage(
  data: OverviewResponse,
  completedRun: RunSummary | null,
): ScanCoverage {
  const reportCoverage = contentFitnessPageCoverage(data);
  const inventoryPages = data.pages.length;
  const runPages = completedRun
    ? completedRun.pageCounts.scraped +
      completedRun.pageCounts.failed +
      completedRun.pageCounts.skipped
    : null;
  const runMatchesInventory = runPages === inventoryPages;

  return {
    inventoryPages,
    completedPages: runMatchesInventory
      ? (completedRun?.pageCounts.scraped ?? null)
      : null,
    failedPages: runMatchesInventory
      ? (completedRun?.pageCounts.failed ?? null)
      : null,
    skippedPages: runMatchesInventory
      ? (completedRun?.pageCounts.skipped ?? null)
      : null,
    assessedPages: reportCoverage.assessedPages,
    scoredPages: reportCoverage.scoredPages,
  };
}

export function scanCoverageLine(coverage: ScanCoverage): string {
  const completed =
    coverage.completedPages === null
      ? `${coverage.inventoryPages} ${pluralize(coverage.inventoryPages, "page")} in inventory`
      : coverage.completedPages === coverage.inventoryPages
        ? `${coverage.completedPages} completed`
        : `${coverage.inventoryPages} in inventory / ${coverage.completedPages} completed`;
  const failures =
    (coverage.failedPages ?? 0) > 0
      ? ` / ${coverage.failedPages} not completed`
      : "";
  return `${completed} / ${coverage.assessedPages} assessed / ${coverage.scoredPages} scored${failures}`;
}

export function scanScopeLines(coverage: ScanCoverage): string[] {
  const lines = [
    `${coverage.inventoryPages} ${pluralize(coverage.inventoryPages, "page")} in inventory`,
    coverage.completedPages === null
      ? `${coverage.assessedPages} assessed / ${coverage.scoredPages} scored`
      : `${coverage.completedPages} completed / ${coverage.assessedPages} assessed / ${coverage.scoredPages} scored`,
  ];
  if ((coverage.failedPages ?? 0) > 0) {
    lines.push(
      `${coverage.failedPages} ${pluralize(coverage.failedPages ?? 0, "page")} not completed`,
    );
  }
  if ((coverage.skippedPages ?? 0) > 0) {
    lines.push(`${coverage.skippedPages} skipped`);
  }
  return lines;
}
